import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Box,
  Chip,
} from "@mui/material";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { Deal } from "../../../validation/customer.validation";
import {formatCurrency} from "../../../helper/currencyFormatter.helper";

interface Props {
  deals: Deal[];
  loading: boolean;
  handleInterestClick: (deal: Deal) => void;
  updateDealStatus?: (dealId: string, status: string) => void;
}

const BankerDealsTableView: React.FC<Props> = ({ deals, loading, handleInterestClick }) => {
  return (
    <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
      <Table sx={{ minWidth: 800 }}>
        <TableHead>
          <TableRow>
            <TableCell>Loan Name</TableCell>
            <TableCell>Deal Type</TableCell>
            <TableCell>Loan Size</TableCell>
            <TableCell>LTV</TableCell>
            <TableCell>DSC Ratio</TableCell>
            <TableCell>Business Name</TableCell>
            <TableCell>Customer Name</TableCell>
            <TableCell>Customer Phone</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography className="text-gray-500">Loading deals...</Typography>
              </TableCell>
            </TableRow>
          ) : deals.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Box className="flex flex-col items-center justify-center py-32 text-center text-gray-500 bg-white rounded-lg border border-gray-200 mx-4">
                  <div className="flex items-center justify-center mb-4">
                    <div className="animate-pulse">
                      <SearchRoundedIcon
                        className="text-gray-400"
                        sx={{ fontSize: 48 }}
                      />
                    </div>
                  </div>
                  <Typography variant="h6" className="mt-4 text-gray-700">
                    No Deals Found
                  </Typography>
                  <Typography variant="body2" className="text-gray-500">
                    You can try adjusting your search or wait for new deals to be assigned.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            deals.map((deal) => (
              <TableRow key={deal.id}>
                <TableCell>{deal.loanName}</TableCell>
                <TableCell>
                  <Chip
                    icon={<HomeWorkIcon />}
                    label={deal.dealType}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${formatCurrency(deal.loanSize)}`}
                    color="secondary"
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    icon={<TrendingUpIcon />}
                    label={`${deal.maxLTV}%`}
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    icon={<AssessmentIcon />}
                    label={deal.minDSCRatio}
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {deal.business ? `${(deal.business as any)?.name || ''}` : "N/A"}
                </TableCell>
                <TableCell>
                  {deal.isShareCustomerDetails ? `${(deal.customer as any)?.firstName || ''} ${(deal.customer as any)?.middleName || ''} ${(deal.customer as any)?.lastName || ''}`.trim() || "N/A" : "N/A"}
                </TableCell>
                <TableCell>
                  {deal.isShareCustomerDetails ? deal.customer?.phone || "N/A" : "N/A"}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="info"
                      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                      onClick={() => window.location.href = `/banker/deals/${deal.id}`}
                    >
                      View Details
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BankerDealsTableView;
